import React, { useState, useEffect, Suspense, lazy } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, ChevronDown, ChevronUp, Plus, Upload, Image, Video, MessageCircle, Music, Music2, Edit } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { styles } from '@/styles/ExampleStudio.stylex'
import { examples, ExampleProject } from './examples'


const ImagePopupRaw = lazy(() => import('@/components/popup/ImagePopup'));
const ImagePopup = (props: any) => (
    <Suspense fallback={null}>
        <ImagePopupRaw {...props} />
    </Suspense>
);


const VideoPopupRaw = lazy(() => import('@/components/popup/VideoPopup'));
const VideoPopup = (props: any) => (
    <Suspense fallback={null}>
        <VideoPopupRaw {...props} />
    </Suspense>
);


const SpeechPopupRaw = lazy(() => import('@/components/popup/SpeechPopup'));
const SpeechPopup = (props: any) => (
    <Suspense fallback={null}>
        <SpeechPopupRaw {...props} />
    </Suspense>
);


const MusicPopupRaw = lazy(() => import('@/components/popup/MusicPopup'));
const MusicPopup = (props: any) => (
    <Suspense fallback={null}>
        <MusicPopupRaw {...props} />
    </Suspense>
);


const SoundEffectPopupRaw = lazy(() => import('@/components/popup/SoundEffectPopup'));
const SoundEffectPopup = (props: any) => (
    <Suspense fallback={null}>
        <SoundEffectPopupRaw {...props} />
    </Suspense>
);

const ExampleStudioContent: React.FC = () => {
    const [searchParams] = useSearchParams()
    const [project, setProject] = useState<ExampleProject | null>(null)
    const [projectDetailsCollapsed, setProjectDetailsCollapsed] = useState(false)
    const [mediaCollapsed, setMediaCollapsed] = useState(false)

    // Popup states
    const [imagePopupOpen, setImagePopupOpen] = useState(false)
    const [videoPopupOpen, setVideoPopupOpen] = useState(false)
    const [speechPopupOpen, setSpeechPopupOpen] = useState(false)
    const [musicPopupOpen, setMusicPopupOpen] = useState(false)
    const [soundEffectPopupOpen, setSoundEffectPopupOpen] = useState(false)
    const [selectedMedia, setSelectedMedia] = useState<any>(null)

    // Load project from URL parameters
    useEffect(() => {
        if (!project && searchParams) {
            const projectId = searchParams.get('id')
            if (projectId) {
                try {
                    const foundProject = examples.find(example => example.id === projectId)
                    if (foundProject) {
                        setProject(foundProject)
                    }
                } catch (error) {
                    console.error('Failed to parse project from URL:', error)
                }
            }
        }
    }, [project, searchParams])

    // Load collapsed states from localStorage on mount
    useEffect(() => {
        const savedProjectDetailsState = localStorage.getItem('exampleProjectDetailsCollapsed')
        const savedMediaState = localStorage.getItem('exampleMediaCollapsed')

        if (savedProjectDetailsState !== null) {
            setProjectDetailsCollapsed(savedProjectDetailsState === 'true')
        }
        if (savedMediaState !== null) {
            setMediaCollapsed(savedMediaState === 'true')
        }
    }, [])

    const toggleProjectDetails = () => {
        const newState = !projectDetailsCollapsed
        setProjectDetailsCollapsed(newState)
        localStorage.setItem('exampleProjectDetailsCollapsed', String(newState))
    }

    const toggleMedia = () => {
        const newState = !mediaCollapsed
        setMediaCollapsed(newState)
        localStorage.setItem('exampleMediaCollapsed', String(newState))
    }

    // Mouse tracking for media shine effect
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top

        e.currentTarget.style.setProperty('--mouse-x', `${x}px`)
        e.currentTarget.style.setProperty('--mouse-y', `${y}px`)
    }
    // Handle opening media popup
    const handleEditMedia = (media: any, mediaType: string) => {
        console.log('media', media);
        setSelectedMedia(media)
        switch (mediaType) {
            case 'Image':
                setImagePopupOpen(true)
                break
            case 'Video':
                setVideoPopupOpen(true)
                break
            case 'Speech':
                setSpeechPopupOpen(true)
                break
            case 'Music':
                setMusicPopupOpen(true)
                break
            case 'SoundEffect':
                setSoundEffectPopupOpen(true)
                break
            default:
                console.warn('Unknown media type:', mediaType)
        }
    }

    // Helper function to get media type specific icon and tooltip
    const getMediaTypeInfo = (mediaType: string) => {
        const mediaIcons: { [key: string]: { icon: any, label: string, color: string } } = {
            'Image': { icon: Image, label: 'Image', color: '#ffd966' },
            'Video': { icon: Video, label: 'Video', color: '#ff79c6' },
            'Speech': { icon: MessageCircle, label: 'Speech', color: '#50fa7b' },
            'Music': { icon: Music, label: 'Music', color: '#c2a7fc' },
            'SoundEffect': { icon: Music2, label: 'Sound Effect', color: '#8be9fd' }
        }

        const config = mediaIcons[mediaType] || { icon: Image, label: 'Media', color: '#6b7280' }
        const IconComponent = config.icon
        return {
            icon: (
                <div {...stylex.props(styles.mediaIconContainer)}>
                    <IconComponent size={24} />
                    <Plus size={12} {...stylex.props(styles.plusIcon)} />
                </div>
            ),
            tooltip: `Add New ${config.label}`,
            color: config.color,
            iconComponent: IconComponent
        }
    }

    // Render media content based on type
    const renderMediaContent = (media: any, mediaType: string) => {
        if (!media.cdn_url) return null

        switch (mediaType) {
            case 'Image':
                return (
                    <img
                        src={media.cdn_url}
                        alt={media.prompt || 'Generated image'}
                        {...stylex.props(styles.taskImage)}
                    />
                )
            case 'Video':
                return (
                    <video
                        src={media.cdn_url}
                        controls
                        {...stylex.props(styles.taskVideo)}
                    >
                        Your browser does not support the video tag.
                    </video>
                )
            case 'Speech':
            case 'Music':
            case 'SoundEffect':
                return (
                    <audio
                        src={media.cdn_url}
                        controls
                        {...stylex.props(styles.taskAudio)}
                    >
                        Your browser does not support the audio tag.
                    </audio>
                )
            default:
                return (
                    <div {...stylex.props(styles.mediaPlaceholder)}>
                        <span>Media Preview</span>
                    </div>
                )
        }
    }

    // Render media items with the new clean style
    const renderMediaItems = (mediaType: string, mediaItems: any[]) => {
        const mediaTypeInfo = getMediaTypeInfo(mediaType)
        const IconComponent = mediaTypeInfo.iconComponent

        return (
            <div {...stylex.props(styles.mediaBox)}>
                {/* Media Content Row */}
                <div {...stylex.props(styles.mediaRow)}>
                    {/* Media Content */}
                    <div {...stylex.props(styles.mediaContent)}>
                        <div {...stylex.props(styles.mediaItemsContainer)}>
                            <div {...stylex.props(styles.addButtonContainer)}>
                                <button
                                    {...stylex.props(styles.mediaAddButton)}
                                    disabled
                                    style={{ color: mediaTypeInfo.color }}
                                    title={mediaTypeInfo.tooltip}>
                                    {mediaTypeInfo.icon}
                                </button>
                            </div>
                            {mediaItems.map((media: any) => (
                                <div key={media.id} {...stylex.props(styles.taskCard)}>
                                    <div {...stylex.props(styles.taskHeader)}>
                                        <div {...stylex.props(styles.taskTypeAndStatus)}>
                                            <div {...stylex.props(styles.taskTypeIcon)} style={{ color: mediaTypeInfo.color }}>
                                                <IconComponent size={16} />
                                            </div>
                                        </div>
                                        <div {...stylex.props(styles.taskActions)}>
                                            <button
                                                {...stylex.props(styles.taskControlButton)}
                                                title="Edit"
                                                onClick={() => handleEditMedia(media, mediaType)}>
                                                <Edit size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    <div {...stylex.props(styles.taskBody)}>
                                        {renderMediaContent(media, mediaType)}
                                        <p {...stylex.props(styles.mediaItemPrompt)}>
                                            {media.prompt || media.description || media.style || 'No description available'}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // Show loading state if project is not loaded yet
    if (!project) {
        return (
            <div {...stylex.props(styles.pageContainer)}>
                <div style={{ padding: 'var(--spacing-md)' }}>
                    <Link to="/examples" {...stylex.props(styles.backLink)}>
                        <ArrowLeft size={20} />
                        Back to Examples
                    </Link>
                </div>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    Please select an example to view!
                </div>
            </div>
        )
    }

    return (
        <>
            <div {...stylex.props(styles.pageContainer)}>
                {/* Navigation Bar */}
                <div style={{ padding: 'var(--spacing-md) 0' }}>
                    <Link to="/examples" {...stylex.props(styles.backLink)}>
                        <ArrowLeft size={20} />
                        Back to Examples
                    </Link>
                </div>

                {/* Project Details Section */}
                <div {...stylex.props(styles.sectionWrapper)}>
                    <div
                        {...stylex.props(styles.shineHeader)}
                        onMouseMove={handleMouseMove}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                                {project?.title}
                            </h2>
                            <h5 style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0, fontWeight: 400 }}>{project?.type}</h5>
                        </div>
                        <button
                            style={{
                                background: 'transparent', border: 0, color: 'var(--text-secondary)', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem', borderRadius: '50%'
                            }}
                            onClick={toggleProjectDetails}
                            aria-label={projectDetailsCollapsed ? "Expand project details" : "Collapse project details"}>
                            {projectDetailsCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                        </button>
                    </div>
                    <div {...stylex.props(styles.sectionContent, projectDetailsCollapsed && styles.sectionContentCollapsed)}>
                        <div {...stylex.props(styles.projectDetailsArea)}>
                            <div {...stylex.props(styles.contentGrid)}>
                                {/* Story Section */}
                                <div {...stylex.props(styles.storyContainer, styles.gridItem)}>
                                    <div {...stylex.props(styles.sectionHeader)}>
                                        <h3 {...stylex.props(styles.sectionHeaderTitle)}>📖 Story</h3>
                                    </div>
                                    <div {...stylex.props(styles.editableContent)}>
                                        <ReactMarkdown>{project?.story}</ReactMarkdown>
                                    </div>
                                </div>

                                {/* Concept Section */}
                                <div {...stylex.props(styles.storyContainer, styles.gridItem)}>
                                    <div {...stylex.props(styles.sectionHeader)}>
                                        <h3 {...stylex.props(styles.sectionHeaderTitle)}>💡 Concept</h3>
                                    </div>
                                    <div {...stylex.props(styles.editableContent)}>
                                        <ReactMarkdown>{project?.concept || ''}</ReactMarkdown>
                                    </div>
                                </div>

                                {/* Screenplay Section */}
                                <div {...stylex.props(styles.storyContainer, styles.gridItem)}>
                                    <div {...stylex.props(styles.sectionHeader)}>
                                        <h3 {...stylex.props(styles.sectionHeaderTitle)}>🎬 Screenplay</h3>
                                    </div>
                                    <div {...stylex.props(styles.editableContent)}>
                                        <ReactMarkdown>{project?.screen_write}</ReactMarkdown>
                                    </div>
                                </div>

                                {/* Metadata Section */}
                                <div {...stylex.props(styles.storyContainer, styles.gridItem)}>
                                    <div {...stylex.props(styles.sectionHeader)}>
                                        <h3 {...stylex.props(styles.sectionHeaderTitle)}>📊 Metadata</h3>
                                    </div>
                                    <div {...stylex.props(styles.editableContent)}>
                                        <div {...stylex.props(styles.metadataList)}>
                                            {/* Project Dimensions */}
                                            <div {...stylex.props(styles.metadataItem)}>
                                                <strong {...stylex.props(styles.metadataStrong)}>Resolution:</strong> {project?.width} × {project?.height}
                                            </div>
                                            <div {...stylex.props(styles.metadataItem)}>
                                                <strong {...stylex.props(styles.metadataStrong)}>Duration:</strong> {project?.duration}s
                                            </div>
                                            {/* Custom Metadata */}
                                            {Object.entries(project.metadata || {}).map(([key, value]) => (
                                                <div key={key} {...stylex.props(styles.metadataItem)}>
                                                    <strong {...stylex.props(styles.metadataStrong)}>{key}:</strong> {String(value)}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Characters Section */}
                                {project?.characters && Object.values(project.characters).map(character => (
                                    <div key={character.id} {...stylex.props(styles.gridItem)}>
                                        <div style={{ backgroundColor: 'var(--bg-element)', padding: '1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
                                            <h4 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-primary)' }}>{character.name}</h4>
                                            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{character.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Media Section */}
                <div {...stylex.props(styles.sectionWrapper)} style={{ marginTop: '2rem' }}>
                    <div {...stylex.props(styles.shineHeader)} onMouseMove={handleMouseMove}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Media</h2>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <button
                                {...stylex.props(styles.uploadButton)}
                                disabled
                                aria-label="Upload Media">
                                <Upload size={18} />
                                Upload Media
                            </button>
                            <button
                                style={{
                                    background: 'transparent', border: 0, color: 'var(--text-secondary)', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem', borderRadius: '50%'
                                }}
                                onClick={toggleMedia}
                                aria-label={mediaCollapsed ? "Expand media" : "Collapse media"}>
                                {mediaCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                            </button>
                        </div>
                    </div>

                    {/* Media Content - Modified to not have a separate scrollable area */}
                    {!mediaCollapsed && (
                        <div {...stylex.props(styles.mediaContainer)}>
                            <div {...stylex.props(styles.mediaScroll)}>
                                {renderMediaItems('Image', Object.values(project?.images || {}).filter((img: any) => img.usage === 'STANDALONE').sort((a: any, b: any) => b.id.localeCompare(a.id)))}
                            </div>
                            <div {...stylex.props(styles.mediaScroll)}>
                                {renderMediaItems('Video', Object.values(project?.videos || {}).sort((a: any, b: any) => b.id.localeCompare(a.id)))}
                            </div>
                            <div {...stylex.props(styles.mediaScroll)}>
                                {renderMediaItems('Speech', Object.values(project?.speechs || {}).sort((a: any, b: any) => b.id.localeCompare(a.id)))}
                            </div>
                            <div {...stylex.props(styles.mediaScroll)}>
                                {renderMediaItems('Music', Object.values(project?.musics || {}).sort((a: any, b: any) => b.id.localeCompare(a.id)))}
                            </div>
                            <div {...stylex.props(styles.mediaScroll)}>
                                {renderMediaItems('SoundEffect', Object.values(project?.sound_effects || {}).sort((a: any, b: any) => b.id.localeCompare(a.id)))}
                            </div>
                        </div>
                    )}
                </div>
                {/* Main Video preview */}
                <div style={{ marginTop: '2rem', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--border)' }}>
                    <video src={project?.videoSrc} controls style={{ width: '100%', display: 'block' }} />
                </div>
            </div>

            {/* Popup Components */}
            {selectedMedia && (
                <>
                    {/* Image Popup */}
                    <ImagePopup
                        isOpen={imagePopupOpen}
                        onClose={() => setImagePopupOpen(false)}
                        id={selectedMedia.id}
                        src={selectedMedia.cdn_url}
                        modelKey={selectedMedia.modelKey}
                        modelLabel={selectedMedia.modelLabel}
                        prompt={selectedMedia.prompt || ''}
                        width={selectedMedia.width || 1024}
                        height={selectedMedia.height || 1024}
                        popupTitle="Image Details"
                        modelNote=""
                        allImages={selectedMedia.reference_images_ids ? Object.values(project?.images || {}).map((img: any) => img.cdn_url) : []}
                        selectedReferenceImages={selectedMedia.reference_images_ids ? Object.values(project?.images || {}).filter((img: any) => selectedMedia.reference_images_ids.includes(img.id)).map((img: any) => img.cdn_url) : []}
                    />

                    {/* Video Popup */}
                    <VideoPopup
                        isOpen={videoPopupOpen}
                        onClose={() => setVideoPopupOpen(false)}
                        id={selectedMedia.id}
                        videoSrc={selectedMedia.cdn_url}
                        modelKey={selectedMedia.modelKey}
                        modelLabel={selectedMedia.modelLabel}
                        prompt={selectedMedia.prompt || ''}
                        popupTitle="Video Details"
                        modelNote=""
                        allImages={selectedMedia.image_id ? Object.values(project?.images || {}).map((img: any) => img.cdn_url) : []}
                        allVideos={selectedMedia.video_id ? Object.values(project?.videos || {}).map((video: any) => video.cdn_url) : []}
                        allAudios={selectedMedia.audio_id ? Object.values(project?.speechs || {}).map((speech: any) => speech.cdn_url) : []}
                        selectedReferenceImages={selectedMedia.image_id ? Object.values(project?.images || {}).filter((img: any) => img.id === selectedMedia.image_id).map((img: any) => img.cdn_url) : []}
                        selectedReferenceVideos={selectedMedia.video_id ? Object.values(project?.videos || {}).filter((video: any) => video.id === selectedMedia.video_id).map((video: any) => video.cdn_url) : []}
                        selectedReferenceAudios={selectedMedia.audio_id ? Object.values(project?.speechs || {}).filter((speech: any) => speech.id === selectedMedia.audio_id).map((speech: any) => speech.cdn_url) : []}
                    />

                    {/* Speech Popup */}
                    <SpeechPopup
                        isOpen={speechPopupOpen}
                        onClose={() => setSpeechPopupOpen(false)}
                        id={selectedMedia.id}
                        audioSrc={selectedMedia.cdn_url}
                        modelKey={selectedMedia.modelKey}
                        modelLabel={selectedMedia.modelLabel}
                        prompt={selectedMedia.prompt || ''}
                        voice={selectedMedia.voice_id}
                        voiceDescription={selectedMedia.voice_description}
                        voice2={selectedMedia.voice_id_2}
                        voice2Description={selectedMedia.voice2_description}
                        tone={selectedMedia.tone}
                        popupTitle="Speech Details"
                        modelNote=""
                    />

                    {/* Music Popup */}
                    <MusicPopup
                        isOpen={musicPopupOpen}
                        onClose={() => setMusicPopupOpen(false)}
                        id={selectedMedia.id}
                        audioSrc1={selectedMedia.cdn_url}
                        modelKey={selectedMedia.modelKey}
                        modelLabel={selectedMedia.modelLabel}
                        prompt={selectedMedia.prompt || ''}
                        style={selectedMedia.style}
                        mode={selectedMedia.mode || 'music'}
                        vocalGender={selectedMedia.vocal_gender}
                        title={selectedMedia.title || 'Generated Music'}
                        popupTitle="Music Details"
                        modelNote=""
                    />

                    {/* Sound Effect Popup */}
                    <SoundEffectPopup
                        isOpen={soundEffectPopupOpen}
                        onClose={() => setSoundEffectPopupOpen(false)}
                        id={selectedMedia.id}
                        audioSrc={selectedMedia.cdn_url}
                        modelKey={selectedMedia.modelKey}
                        modelLabel={selectedMedia.modelLabel}
                        prompt={selectedMedia.prompt || ''}
                        voice={selectedMedia.voice_id}
                        voiceDescription={selectedMedia.voice_description}
                        tone={selectedMedia.tone}
                        popupTitle="Sound Effect Details"
                        modelNote=""
                    />
                </>
            )
            }
        </>
    )
}

const ExampleStudio: React.FC = () => {
    return (
        <Suspense fallback={<div>Loading...</div>}>
            <ExampleStudioContent />
        </Suspense>
    )
}

export default ExampleStudio
